import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Alert,
  Chip
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import HolidayService from '../../services/HolidayService.js';
import { useAuth } from '../../contexts/AuthContext.js';

const HolidayManagement = () => {
  // Get current user and authentication state
  const { currentUser, isAuthenticated } = useAuth();
  // Check for Administrator role - handle different role formats (string, array of strings, or array of objects)
  const isAdmin = currentUser?.roles ? (
    Array.isArray(currentUser.roles)
      ? currentUser.roles.some(role => 
          typeof role === 'string'
            ? role === 'Administrator'
            : (role && typeof role === 'object' && (role.name || role.roleName || '') === 'Administrator')
        )
      : typeof currentUser.roles === 'string' && currentUser.roles === 'Administrator'
  ) : false;
  
  // Debug role information
  useEffect(() => {
    if (currentUser?.roles) {
      console.log('Current user roles:', currentUser.roles);
      console.log('Is admin detected:', isAdmin);
    }
  }, [currentUser, isAdmin]);
  
  // State for holidays list
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // State for holiday form dialog
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('Add Holiday');
  const [formData, setFormData] = useState({
    holidayId: 0,
    name: '',
    date: null,
    endDate: null,
    description: '',
    isRecurringYearly: false
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  
  // State for delete confirmation dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Fetch holidays on component mount
  useEffect(() => {
    fetchHolidays();
  }, []);
  
  // Function to fetch all holidays
  const fetchHolidays = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await HolidayService.getAllHolidays();
      setHolidays(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching holidays:', error);
      setError('Failed to load holidays. Please try again later.');
      setLoading(false);
    }
  };
  
  // Handle pagination change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Open dialog to add a new holiday
  const handleAddHoliday = () => {
    setDialogTitle('Add Holiday');
    setFormData({
      holidayId: 0,
      name: '',
      date: null,
      endDate: null,
      description: '',
      isRecurringYearly: false
    });
    setFormError(null);
    setOpenDialog(true);
  };
  
  // Open dialog to edit an existing holiday
  const handleEditHoliday = (holiday) => {
    setDialogTitle('Edit Holiday');
    setFormData({
      holidayId: holiday.holidayId,
      name: holiday.name,
      date: new Date(holiday.date),
      endDate: holiday.endDate || holiday.EndDate ? new Date(holiday.endDate || holiday.EndDate) : null,
      description: holiday.description || '',
      isRecurringYearly: holiday.isRecurringYearly || false
    });
    setFormError(null);
    setOpenDialog(true);
  };
  
  // Open dialog to confirm holiday deletion
  const handleDeleteClick = (holiday) => {
    setHolidayToDelete(holiday);
    setOpenDeleteDialog(true);
  };
  
  // Close the form dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  
  // Close the delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setHolidayToDelete(null);
  };
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'isRecurringYearly' && type === 'checkbox' && checked ? { endDate: null } : {})
    });
  };
  
  // Handle date picker changes
  const handleDateChange = (date) => {
    console.log('Date selected in picker:', date);
    setFormData({
      ...formData,
      date: date
    });
  };

  const handleEndDateChange = (date) => {
    setFormData({
      ...formData,
      endDate: date
    });
  };
  
  // Submit the holiday form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    
    // Check if user has administrator role
    if (!isAdmin) {
      setFormError('You do not have permission to manage holidays. Administrator role is required.');
      setSaving(false);
      return;
    }
    
    // Validate form data
    if (!formData.name || !formData.date) {
      setFormError('Please fill in all required fields.');
      setSaving(false);
      return;
    }
    
    // Validate date is a valid Date object
    if (!(formData.date instanceof Date) || isNaN(formData.date.getTime())) {
      setFormError('Please select a valid date.');
      setSaving(false);
      return;
    }

    if (formData.endDate) {
      if (!(formData.endDate instanceof Date) || isNaN(formData.endDate.getTime())) {
        setFormError('Please select a valid end date.');
        setSaving(false);
        return;
      }

      const startOnly = new Date(formData.date);
      startOnly.setHours(0, 0, 0, 0);
      const endOnly = new Date(formData.endDate);
      endOnly.setHours(0, 0, 0, 0);

      if (endOnly < startOnly) {
        setFormError('End date must be the same as or after the start date.');
        setSaving(false);
        return;
      }

      if (formData.isRecurringYearly && endOnly.getTime() !== startOnly.getTime()) {
        setFormError('Recurring yearly holidays cannot have a date range. Please remove end date or turn off recurring.');
        setSaving(false);
        return;
      }
    }
    
    console.log('Submitting holiday with date:', formData.date);
    
    try {
      if (formData.holidayId === 0) {
        // Create new holiday
        await HolidayService.createHoliday(formData);
      } else {
        // Update existing holiday
        await HolidayService.updateHoliday(formData.holidayId, formData);
      }
      
      // Refresh the holidays list
      await fetchHolidays();
      
      // Close the dialog
      setOpenDialog(false);
      setSaving(false);
    } catch (error) {
      console.error('Error saving holiday:', error);
      
      // Display more specific error messages
      if (error.message && error.message.includes('permission')) {
        setFormError('You do not have permission to manage holidays. Administrator role is required.');
      } else if (error.response && error.response.status === 403) {
        setFormError('Access denied. You need administrator privileges to perform this action.');
      } else {
        setFormError('Failed to save holiday. Please try again.');
      }
      
      setSaving(false);
    }
  };
  
  // Delete a holiday
  const handleDeleteHoliday = async () => {
    if (!holidayToDelete) return;
    
    setDeleting(true);
    
    try {
      await HolidayService.deleteHoliday(holidayToDelete.holidayId);
      
      // Refresh the holidays list
      await fetchHolidays();
      
      // Close the dialog
      setOpenDeleteDialog(false);
      setHolidayToDelete(null);
      setDeleting(false);
    } catch (error) {
      console.error('Error deleting holiday:', error);
      setError('Failed to delete holiday. Please try again.');
      setDeleting(false);
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const formatDateRange = (holiday) => {
    const start = holiday.date || holiday.Date;
    const end = holiday.endDate || holiday.EndDate;
    if (!end) return formatDate(start);
    const startLabel = formatDate(start);
    const endLabel = formatDate(end);
    return startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`;
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ display: 'inline-block', mr: 2 }}>
            Holiday Management
          </Typography>
          {isAdmin ? (
            <Chip color="success" label="Administrator Access" size="small" sx={{ verticalAlign: 'middle' }} />
          ) : (
            <Chip color="error" label="Read-Only Access" size="small" sx={{ verticalAlign: 'middle' }} />
          )}
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddHoliday}
          disabled={!isAdmin}
        >
          Add Holiday
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {holidays.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No holidays found. Click "Add Holiday" to create one.
                    </TableCell>
                  </TableRow>
                ) : (
                  holidays
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((holiday) => (
                      <TableRow key={holiday.holidayId}>
                        <TableCell>{holiday.name}</TableCell>
                        <TableCell>{formatDateRange(holiday)}</TableCell>
                        <TableCell>{holiday.description}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            color="primary"
                            onClick={() => handleEditHoliday(holiday)}
                            size="small"
                            disabled={!isAdmin}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteClick(holiday)}
                            size="small"
                            disabled={!isAdmin}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={holidays.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
      
      {/* Add/Edit Holiday Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Holiday Name *"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={saving}
                />
              </Grid>
              
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Start Date *"
                    value={formData.date}
                    onChange={handleDateChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        required
                        disabled={saving}
                      />
                    )}
                    disabled={saving}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="End Date (optional)"
                    value={formData.endDate}
                    onChange={handleEndDateChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        disabled={saving || formData.isRecurringYearly}
                      />
                    )}
                    disabled={saving || formData.isRecurringYearly}
                  />
                </LocalizationProvider>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  disabled={saving}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    id="isRecurringYearly"
                    name="isRecurringYearly"
                    checked={formData.isRecurringYearly}
                    onChange={handleInputChange}
                    style={{ marginRight: '8px' }}
                    disabled={saving}
                  />
                  <label htmlFor="isRecurringYearly">Recurring Yearly</label>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the holiday "{holidayToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteHoliday}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HolidayManagement;
